import type { ReportConfigSceneScreenshotValue } from './ReportConfigSceneScreenshotValue';
import type { IConfigProps, IReportValueAccessor, IScreenshotBuilder } from './interfaces';
import type { IViewPortOrientation } from '@/engine/app/interfaces';

/**
 * Represents a class that provides access to report canvas screenshots.
 */
export class ReportCanvasScreenshotAccessor implements IReportValueAccessor {
  private _screenshotBuilder: IScreenshotBuilder;

  /**
   * Initializes a new instance of the ReportCanvasScreenshotAccessor class.
   * @param screenshotBuilder The screenshot builder used to capture screenshots.
   */
  public constructor(screenshotBuilder: IScreenshotBuilder) {
    this._screenshotBuilder = screenshotBuilder;
  }

  public init(): void {
    this._screenshotBuilder.init();
  }

  /**
   * Gets the value by the specified report configuration entry.
   * @param config The report configuration entry.
   * @returns A promise that resolves to the value of the screenshot.
   */
  public async getValueByConfig(config: IConfigProps): Promise<unknown> {
    const reportConfig = config as ReportConfigSceneScreenshotValue;

    if (!reportConfig.viewDescriptor || !reportConfig.items || !reportConfig.absoluteDimensions) {
      throw new Error('Logic Error. Config is incomplete.');
    }

    const screenshotBuilder = await this._screenshotBuilder.switchView(reportConfig.viewDescriptor);

    screenshotBuilder
      .reset()
      .setViewPortDescriptor(reportConfig.viewportDescriptor)
      .setItems(reportConfig.items)
      .setDimensions(reportConfig.absoluteDimensions);

    if (reportConfig.phase && (reportConfig.phase === 'endSystole' || reportConfig.phase === 'endDiastole')) {
      // this async operation should be executed in prior to the following
      await screenshotBuilder.switchPhaseByKind(reportConfig.phase);
    }

    if ((reportConfig.viewRoi || reportConfig.viewRoiOffset) && reportConfig.orientation) {
      throw new Error('Logic error. Ambiguity: either View ROI or Orientation expected, not both.');
    }

    if (reportConfig.viewRoi && reportConfig.viewRoiOffset) {
      throw new Error('Logic error. Ambiguity: only one type of View ROI expected.');
    }

    if (reportConfig.orientation && reportConfig.sliceOrientation && reportConfig.orientationByPreset) {
      throw new Error('Either `orientation` or `sliceOrientation` or `orientationByPreset`expected, not all.');
    }

    if (reportConfig.viewRoi) {
      await screenshotBuilder.setViewRoi(reportConfig.viewRoi.locationDescriptor, reportConfig.viewRoi.zoomFactor);
    }

    if (reportConfig.viewRoiOffset) {
      await screenshotBuilder.setViewRoiOffset(reportConfig.viewRoiOffset.locationDescriptor);
    }

    if (reportConfig.syncImagesPoint) {
      await screenshotBuilder.syncImages(reportConfig.syncImagesPoint);
    }

    if (reportConfig.orientation) {
      screenshotBuilder.setCamera(reportConfig.orientation);
    }

    if (reportConfig.sliceOrientation) {
      await screenshotBuilder.setSliceOrientation(
        reportConfig.sliceOrientation.sliceType,
        reportConfig.sliceOrientation.orientationByPreset
      );
    }

    if (reportConfig.orientationByPreset) {
      const orientation: IViewPortOrientation = this._screenshotBuilder.resolvePresetOrientation(
        reportConfig.orientationByPreset,
        true
      );
      screenshotBuilder.setCamera(orientation);
    }

    return await screenshotBuilder.getScreenshot();
  }
}
